import React, { useEffect, useState } from "react";
import { X, Lightning, Check, Sparkle } from "@phosphor-icons/react";
import API from "../api";
import { useAuth } from "../auth/AuthContext";

export default function BillingModal({ open, onClose }) {
  const { user } = useAuth();
  const [packs, setPacks] = useState([]);
  const [busy, setBusy] = useState(false);
  const [busyPackId, setBusyPackId] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open) return;
    API.get("/billing/packs").then((r) => setPacks(r.data.packs || [])).catch(() => {});
  }, [open]);

  if (!open) return null;

  const checkout = async (pack) => {
    setErr("");
    if (!user) { setErr("Sign in first."); return; }
    setBusy(true);
    setBusyPackId(pack.id);
    try {
      const { data } = await API.post("/billing/checkout", {
        pack_id: pack.id,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
      setBusy(false);
      setBusyPackId("");
    }
  };

  // v19.17 — The billing modal is the "Buy generation credits" flow so
  // only surface credit packs here. The Producer Package (grants_daw)
  // has its own dedicated paywall (ProducerPaywallModal). Sort ascending
  // by amount so the cheapest pack is first — feels like a natural
  // upgrade ladder, and the featured "best value" tier ($100 → 1000
  // beats) lands as the last card.
  const creditPacks = (packs || [])
    .filter((p) => !p.grants_daw)
    .sort((a, b) => (a.amount || 0) - (b.amount || 0));

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center px-4"
      style={{ background: "rgba(0,0,0,0.72)", backdropFilter: "blur(8px)" }}
      onClick={onClose}
      data-testid="billing-modal"
    >
      <div
        className="relative w-[min(640px,96vw)] rounded-2xl p-7 text-white"
        style={{
          background: "linear-gradient(180deg,#101010,#070707)",
          border: "1.5px solid #2a2a2a",
          boxShadow: "0 30px 60px rgba(0,0,0,0.5)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <button onClick={onClose} className="absolute top-4 right-4 text-white/40 hover:text-white" data-testid="billing-close" aria-label="Close">
          <X size={18} weight="bold" />
        </button>
        <div className="mb-5">
          <h2 className="font-display font-black text-2xl tracking-tight flex items-center gap-2">
            <Lightning size={22} weight="fill" className="text-yellow-300" /> Buy generation credits
          </h2>
          <p className="text-xs text-white/40 mt-1">One-time payment · credits never expire</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {creditPacks.map((p) => {
            const isFeatured = !!p.featured;
            const isBusyThis = busy && busyPackId === p.id;
            const perBeat = p.credits ? (p.amount / p.credits) : 0;
            return (
              <button
                key={p.id}
                onClick={() => checkout(p)}
                disabled={busy}
                className="text-left rounded-xl p-5 transition relative disabled:opacity-70"
                style={{
                  background: isFeatured
                    ? "linear-gradient(180deg,#0d1a12,#050a08)"
                    : "#0c0c0c",
                  border: isFeatured ? "1.5px solid #34d399" : "1.5px solid #1f1f1f",
                  boxShadow: isFeatured
                    ? "0 0 24px rgba(52,211,153,0.25)"
                    : "0 0 0 1px transparent",
                }}
                data-testid={`pack-${p.id}`}
                onMouseEnter={(e) => {
                  if (isFeatured) return;
                  e.currentTarget.style.borderColor = "#34d399";
                  e.currentTarget.style.boxShadow = "0 0 24px rgba(52,211,153,0.35)";
                }}
                onMouseLeave={(e) => {
                  if (isFeatured) return;
                  e.currentTarget.style.borderColor = "#1f1f1f";
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                {isFeatured && (
                  <div
                    className="absolute -top-2.5 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest text-black flex items-center gap-1"
                    style={{ background: "#34d399", boxShadow: "0 0 12px rgba(52,211,153,0.45)" }}
                    data-testid={`pack-${p.id}-badge-featured`}
                  >
                    <Sparkle size={9} weight="fill" /> Best value
                  </div>
                )}
                <div className="text-[10px] uppercase tracking-wider text-emerald-300 font-mono">{p.label}</div>
                <div className="text-3xl font-display font-black mt-1">${p.amount.toFixed(0)}</div>
                <div className="text-xs text-white/40 font-mono mt-1" data-testid={`pack-${p.id}-credits`}>{p.credits.toLocaleString()} generations</div>
                <div className="mt-3 text-[11px] text-white/50 flex items-center gap-1">
                  <Check size={11} weight="bold" /> ${perBeat.toFixed(2)} per beat
                </div>
                {isBusyThis && <div className="absolute inset-0 rounded-xl flex items-center justify-center bg-black/50 text-emerald-300 text-xs font-mono">Redirecting…</div>}
              </button>
            );
          })}
        </div>
        {err && <div className="text-xs text-red-400 mt-3" data-testid="billing-error">{err}</div>}
        <div className="text-[10px] text-white/30 mt-5 font-mono">
          Powered by Stripe · secure checkout
        </div>
      </div>
    </div>
  );
}
